import { useState } from 'react';
import { api } from '../api/client.js';
import {
  OE_PROFILE_DEFINITIONS,
  type OeElastomerCode,
  type OeProfileDefinition,
} from '@bqs/pricing';
import type {
  OeSealCartLine,
  PricingPreview,
  PricingPreviewItemDetail,
} from '../api/types.js';
import oeSchnittdarstellung from '@bqs/templates/assets/products/oe-schnittdarstellung.png';

/**
 * G-ST-P/OE, ADR-0022: Konfigurator fuer die Stuetzringdichtung.
 *
 * G-ST-P/OE ist eine frei konfigurierte Sonderdichtung, kein Tabellenkatalog --
 * eine einzelne Karte statt einer Produkttabelle. Ring- und Profilpreisraster
 * sowie die Geometrieklassen sind interne Berechnungs- und Admininformationen
 * und erscheinen hier bewusst nicht.
 *
 * Dritter Einstieg neben gefuehrter Auswahl und Freitextsuche. Ruft dieselbe
 * `/api/pricing/preview` wie die uebrigen Positionen -- Geometrie, Mengenstaffel
 * und Rabatt entstehen ausschliesslich serverseitig, hier steht nur die Eingabe
 * und die Anzeige der Antwort.
 *
 * Eingegebene Masse sind die Masse der fertigen Dichtung (Innendurchmesser x
 * Aussendurchmesser), nicht die des Rings oder des Profils -- siehe
 * `@bqs/pricing` `computeOeGeometry`.
 *
 * Die drei Geometriefelder sitzen als Karten direkt an den Masslinien der
 * technischen Schnittdarstellung (prozentual verankert, nicht pixelfest --
 * dieselbe Zeichnung bleibt bewusst unskaliert/schematisch). Auf schmalen
 * Bildschirmen (`theme.css`, 40rem) loesen sie sich von der Zeichnung und
 * stehen als normale gestapelte Formularfelder darunter.
 */
const ELASTOMER_LABEL: Record<OeElastomerCode, string> = {
  '04': 'EPDM',
  '13': 'NBR',
  '08': 'FKM',
};

/**
 * `profil` wird nur fuer Meldungen gebraucht, die eine profilabhaengige
 * Kenngroesse nennen (z. B. den Mindestinnendurchmesser) -- sonst reicht der
 * Status allein.
 *
 * `detail` traegt bei den sieben Geometriegrenzverletzungen den tatsaechlich
 * berechneten Wert und die verletzte Grenze (`OeGeometryFailureDetail` in
 * `@bqs/pricing`, ueber `/api/pricing/preview` durchgereicht) -- die Meldung
 * nennt beide, statt nur zu sagen, dass etwas nicht passt. Fehlt `detail`
 * ausnahmsweise, faellt die Meldung auf den allgemeinen Satz zurueck.
 */
function erklaerungOe(
  status: string,
  profil: OeProfileDefinition | undefined,
  detail: PricingPreviewItemDetail | undefined,
): string {
  switch (status) {
    case 'oe_profile_unknown':
      return 'Unbekanntes Profil.';
    case 'oe_invalid_target_diameters':
      return 'Der Außendurchmesser der Dichtung muss größer als ihr Innendurchmesser sein.';
    case 'oe_invalid_quantity':
      return 'Die Menge muss eine positive ganze Zahl sein.';
    case 'oe_seal_inner_diameter_below_profile_minimum':
      if (detail !== undefined && profil !== undefined) {
        return (
          `Der eingegebene Innendurchmesser beträgt ${detail.actual} mm. Für das ` +
          `Profil ${profil.profileWidthMm} × ${profil.nominalBoreLabel} sind ` +
          `mindestens ${detail.limit} mm erforderlich.`
        );
      }
      return profil === undefined
        ? 'Der Innendurchmesser ist für dieses Profil zu klein.'
        : `Für das Profil ${profil.profileWidthMm} × ${profil.nominalBoreLabel} beträgt der Mindestinnendurchmesser ${profil.minInnerDiameterMm} mm.`;
    case 'oe_seal_outer_diameter_below_profile':
      if (detail !== undefined) {
        return (
          `Der eingegebene Außendurchmesser beträgt ${detail.actual} mm. Für dieses ` +
          `Profil sind bei diesem Innendurchmesser mindestens ${detail.limit} mm ` +
          `erforderlich. Bitte Außendurchmesser vergrößern oder ein schmaleres ` +
          `Profil wählen.`
        );
      }
      return 'Der gewählte Außendurchmesser ist zu klein für dieses Profil bei diesem Innendurchmesser. Bitte Außendurchmesser vergrößern oder ein schmaleres Profil wählen.';
    case 'oe_below_minimum_diameter':
      if (detail !== undefined) {
        return (
          `Der berechnete Ring-Innendurchmesser beträgt ${detail.actual} mm. Für ` +
          `diese Baureihe sind mindestens ${detail.limit} mm erforderlich.`
        );
      }
      return 'Bei diesem Innendurchmesser ist keine Dichtung dieser Baureihe möglich (zu klein).';
    case 'oe_above_maximum_diameter':
      if (detail !== undefined) {
        return (
          `Der berechnete Ring-Innendurchmesser beträgt ${detail.actual} mm. Für ` +
          `diese Baureihe sind höchstens ${detail.limit} mm zulässig.`
        );
      }
      return 'Bei diesem Innendurchmesser ist keine Dichtung dieser Baureihe möglich (zu groß).';
    case 'oe_ring_width_exceeds_class':
      if (detail !== undefined) {
        return (
          `Die berechnete Ringbreite beträgt ${detail.actual} mm. Für die gewählte ` +
          `Geometrie sind maximal ${detail.limit} mm zulässig.`
        );
      }
      return 'Diese Kombination aus Innen- und Außendurchmesser ist für dieses Profil nicht herstellbar.';
    case 'oe_ring_outer_diameter_exceeds_class':
      if (detail !== undefined) {
        return (
          `Der berechnete Ring-Außendurchmesser beträgt ${detail.actual} mm. Für ` +
          `die gewählte Geometrie sind maximal ${detail.limit} mm zulässig.`
        );
      }
      return 'Diese Kombination aus Innen- und Außendurchmesser ist für dieses Profil nicht herstellbar.';
    case 'oe_sheet_thickness_not_allowed_for_class':
      if (detail !== undefined) {
        return (
          `Dieses Profil verlangt eine Blechdicke von ${detail.actual} mm. Für die ` +
          `berechnete Geometrieklasse sind nur ${detail.limit} mm zulässig.`
        );
      }
      return 'Dieses Profil ist bei diesen Abmessungen nicht lieferbar. Bitte ein anderes Profil wählen.';
    case 'oe_no_active_price_version':
      return 'Für G-ST-P/OE ist aktuell kein Preis hinterlegt. Bitte beim Betreiber melden.';
    case 'oe_ring_price_missing':
    case 'oe_profile_price_missing':
      return 'Für diese Kombination ist kein Preis hinterlegt. Bitte beim Betreiber anfragen.';
    default:
      return `Nicht kalkulierbar (${status}).`;
  }
}

/** Welches Geometriefeld ein abgelehnter Preisvorschau-Status betrifft -- nur
 *  fuer die sichtbare Randmarkierung, die Meldung selbst bleibt unveraendert
 *  in `erklaerungOe`. Statuscodes ohne erkennbaren Feldbezug (Profil,
 *  Stueckzahl, Preisdaten) markieren kein Feld. */
function fehlerhaftesFeld(status: string): 'inner' | 'outer' | 'both' | null {
  switch (status) {
    case 'oe_seal_inner_diameter_below_profile_minimum':
    case 'oe_below_minimum_diameter':
    case 'oe_above_maximum_diameter':
      return 'inner';
    case 'oe_seal_outer_diameter_below_profile':
      return 'outer';
    case 'oe_invalid_target_diameters':
    case 'oe_ring_width_exceeds_class':
    case 'oe_ring_outer_diameter_exceeds_class':
      return 'both';
    default:
      return null;
  }
}

const OE_ERROR_MESSAGE_ID = 'oe-error-message';

export function OeConfigurator({
  freight,
  onTakeOver,
  discountPercent,
}: {
  /**
   * Verpackung und Fracht Inland: ausschliesslich fuer interne Rollen
   * (Schnellangebot, siehe `QuickQuotePage`) -- derselbe Konfigurator wie im
   * Kundenbereich, nur mit diesem und dem `discountPercent`-Feld zusaetzlich.
   * Der Kundenworkflow (`CustomerApp`) laesst `freight` bewusst weg: Ein
   * Kontobenutzer darf Verpackung und Fracht weder eingeben noch
   * ueberschreiben, dafuer gilt ausschliesslich der globale Vorgabewert
   * (ADR-0015). Ohne einen globalen Wert lehnt die Vorschau mit
   * `no_packaging_freight_amount` ab, siehe `SelectionList`.
   */
  freight?: string;
  onTakeOver: (line: OeSealCartLine) => void;
  /**
   * Schnellangebot fuer interne Rollen: der frei vergebene Rabattsatz fuer die
   * Vorschau, ohne Konto -- derselbe Konfigurator wie im Kundenbereich, nur mit
   * diesem einen zusaetzlichen Feld. Ein Kontobenutzer bekommt seinen Rabatt
   * weiterhin ausschliesslich aus der eigenen Kondition und braucht dieses Feld
   * nicht -- weggelassen bedeutet unveraendertes Verhalten.
   */
  discountPercent?: number;
}): React.JSX.Element {
  const [targetInnerDiameterMm, setTargetInnerDiameterMm] = useState('');
  const [targetOuterDiameterMm, setTargetOuterDiameterMm] = useState('');
  const [elastomerCode, setElastomerCode] = useState<OeElastomerCode>('04');
  const profileOptions = OE_PROFILE_DEFINITIONS.filter(
    (p) => p.elastomerCode === elastomerCode,
  );
  const [profileArticleCode, setProfileArticleCode] = useState(
    profileOptions[0]?.profileArticleCode ?? '',
  );
  const [ringMaterial, setRingMaterial] = useState<'STEEL_1_4301' | 'STEEL_1_4571'>(
    'STEEL_1_4301',
  );
  const [quantity, setQuantity] = useState('1');
  const [preview, setPreview] = useState<PricingPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function elastomerWaehlen(code: OeElastomerCode): void {
    setElastomerCode(code);
    const erstesProfil = OE_PROFILE_DEFINITIONS.find((p) => p.elastomerCode === code);
    setProfileArticleCode(erstesProfil?.profileArticleCode ?? '');
    setPreview(null);
  }

  function position() {
    return {
      kind: 'oe_seal' as const,
      targetOuterDiameterMm,
      targetInnerDiameterMm,
      quantity,
      profileArticleCode,
      ringMaterial,
    };
  }

  async function berechnen(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<PricingPreview>('/api/pricing/preview', {
        positions: [position()],
        ...(freight !== undefined && freight.trim() !== ''
          ? { packagingFreightOverride: freight.trim() }
          : {}),
        ...(discountPercent !== undefined ? { discountPercent } : {}),
      });
      setPreview(result);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function uebernehmen(): void {
    onTakeOver({
      localId:
        typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : String(Date.now()),
      targetOuterDiameterMm,
      targetInnerDiameterMm,
      quantity,
      profileArticleCode,
      ringMaterial,
    });
    setPreview(null);
  }

  const eingabenVollstaendig =
    targetOuterDiameterMm.trim() !== '' &&
    targetInnerDiameterMm.trim() !== '' &&
    profileArticleCode !== '' &&
    quantity.trim() !== '';

  const ringZeile = preview?.items.find((i) => i.oeRole === 'RING');
  const profilZeile = preview?.items.find((i) => i.oeRole === 'PROFILE');
  const fehlerZeile = preview?.items.find((i) => i.status !== 'ok');
  const gewaehltesProfil = profileOptions.find(
    (p) => p.profileArticleCode === profileArticleCode,
  );
  const betroffenesFeld =
    fehlerZeile !== undefined ? fehlerhaftesFeld(fehlerZeile.status) : null;
  const innenUngueltig = betroffenesFeld === 'inner' || betroffenesFeld === 'both';
  const aussenUngueltig = betroffenesFeld === 'outer' || betroffenesFeld === 'both';

  return (
    <section className="card">
      <h3>G-ST-P/OE – Profildichtung mit offener Einlage</h3>
      <p className="note">
        Frei konfigurierte Sonderdichtung nach Mass. Dichtlippe und Stuetzring werden
        gemeinsam als eine Dichtung in den Warenkorb uebernommen und wie ein
        Katalogartikel rabattiert.
      </p>

      <div className="oe-layout">
        <div className="oe-drawing-panel">
          <div className="oe-drawing-frame">
            <img
              className="oe-drawing-img"
              src={oeSchnittdarstellung}
              alt="Technische Schnittdarstellung der G-ST-P/OE-Dichtung mit Innendurchmesser, Außendurchmesser und Profilquerschnitt"
              decoding="async"
            />

            {/*
              Reihenfolge im Markup ist bewusst Innen-Ø, Außen-Ø, Profil --
              genau die Reihenfolge, in der die drei Felder auf kleinen
              Bildschirmen gestapelt erscheinen, sobald `.oe-anchor` dort von
              `position: absolute` auf normalen Textfluss umschaltet
              (theme.css, 40rem). Die Bildunterschrift steht direkt danach im
              Markup vor den Ankerkarten, damit sie auf kleinen Bildschirmen
              zwischen Zeichnung und Feldern steht; auf dem Desktop haengt sie
              per `top: 100%` ohnehin unter dem Bild, unabhaengig von den
              Ankerkarten daneben.
            */}
            <p className="note oe-drawing-caption">
              Schematische Darstellung, nicht maßstäblich
            </p>

            <div className="oe-anchor oe-anchor-inner">
              <label className="oe-field-card" htmlFor="oe-inner-diameter">
                <span className="oe-field-label">Innen-Ø</span>
                <input
                  id="oe-inner-diameter"
                  type="text"
                  inputMode="decimal"
                  value={targetInnerDiameterMm}
                  onChange={(e) => {
                    setTargetInnerDiameterMm(e.target.value);
                    setPreview(null);
                  }}
                  placeholder="z. B. 300"
                  aria-invalid={innenUngueltig ? 'true' : 'false'}
                  aria-describedby={innenUngueltig ? OE_ERROR_MESSAGE_ID : undefined}
                />
                <span className="oe-unit">mm</span>
              </label>
            </div>

            <div className="oe-anchor oe-anchor-outer">
              <label className="oe-field-card" htmlFor="oe-outer-diameter">
                <span className="oe-field-label">Außen-Ø</span>
                <input
                  id="oe-outer-diameter"
                  type="text"
                  inputMode="decimal"
                  value={targetOuterDiameterMm}
                  onChange={(e) => {
                    setTargetOuterDiameterMm(e.target.value);
                    setPreview(null);
                  }}
                  placeholder="z. B. 400"
                  aria-invalid={aussenUngueltig ? 'true' : 'false'}
                  aria-describedby={aussenUngueltig ? OE_ERROR_MESSAGE_ID : undefined}
                />
                <span className="oe-unit">mm</span>
              </label>
            </div>

            <div className="oe-anchor oe-anchor-profile">
              <label className="oe-field-card oe-field-card--select" htmlFor="oe-profile">
                <span className="oe-field-label">Profil</span>
                <select
                  id="oe-profile"
                  value={profileArticleCode}
                  onChange={(e) => {
                    setProfileArticleCode(e.target.value);
                    setPreview(null);
                  }}
                >
                  {profileOptions.map((p) => (
                    <option key={p.profileArticleCode} value={p.profileArticleCode}>
                      {p.profileWidthMm}×{p.nominalBoreLabel}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </div>

        <div className="oe-side-panel">
          <label>
            Elastomerwerkstoff
            <select
              value={elastomerCode}
              onChange={(e) => elastomerWaehlen(e.target.value as OeElastomerCode)}
            >
              {(Object.keys(ELASTOMER_LABEL) as OeElastomerCode[]).map((code) => (
                <option key={code} value={code}>
                  {ELASTOMER_LABEL[code]}
                </option>
              ))}
            </select>
          </label>
          <label>
            Stützringwerkstoff
            <select
              value={ringMaterial}
              onChange={(e) => {
                setRingMaterial(e.target.value as 'STEEL_1_4301' | 'STEEL_1_4571');
                setPreview(null);
              }}
            >
              <option value="STEEL_1_4301">1.4301</option>
              <option value="STEEL_1_4571">1.4571</option>
            </select>
          </label>
          <label>
            Stückzahl
            <input
              type="text"
              inputMode="numeric"
              className="qty"
              value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value);
                setPreview(null);
              }}
            />
          </label>

          {error !== null && <p role="alert">{error}</p>}

          <div className="row">
            <button
              type="button"
              className="primary oe-calc-button"
              disabled={!eingabenVollstaendig || busy}
              onClick={() => void berechnen()}
            >
              Vorschau berechnen
            </button>
          </div>
        </div>
      </div>

      {preview !== null && (
        <div className="preview-panel">
          {fehlerZeile !== undefined ? (
            <p role="alert" id={OE_ERROR_MESSAGE_ID}>
              {erklaerungOe(fehlerZeile.status, gewaehltesProfil, fehlerZeile.detail)}
            </p>
          ) : (
            <>
              <table>
                <thead>
                  <tr>
                    <th scope="col">Position</th>
                    <th scope="col">Menge</th>
                    <th scope="col">Listenpreis</th>
                    <th scope="col">Rabatt</th>
                    <th scope="col">Positionswert</th>
                  </tr>
                </thead>
                <tbody>
                  {[profilZeile, ringZeile]
                    .filter((z) => z !== undefined)
                    .map((item) => (
                      <tr key={item!.articleNumber}>
                        <th scope="row">
                          <code>{item!.articleNumber}</code>
                        </th>
                        <td className="num">{item!.quantity}</td>
                        <td className="num">{item!.listPrice} €</td>
                        <td className="num">{item!.discountPercent} %</td>
                        <td className="num">{item!.lineNet} €</td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <div className="row">
                <button type="button" className="primary" onClick={uebernehmen}>
                  In den Warenkorb übernehmen
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
